<template>
  <div class="h-[60vh]">
    <ag-grid-vue
      theme="legacy"
      :rowData="drawItems"
      :rowClassRules="rowClassRules"
      :columnDefs="colDefs"
      :defaultColDef="defaultColDefs"
      :autoSizeStrategy="{ type: 'fitGridWidth' }"
      :suppressCellFocus="true"
      :stopEditingWhenCellsLoseFocus="true"
      :suppressDragLeaveHidesColumns="true"
      :getRowHeight="getRowHeight"
      :pinnedBottomRowData="footers"
      rowSelection="single"
      class="pb-3 h-full"
      :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
      @grid-size-changed="resize"
      @grid-ready="(event: GridReadyEvent) => gridApi = event.api"
    ></ag-grid-vue>
  </div>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import type { DrawItem } from "@/types/DrawItem";
import { usePropsStore } from "@/stores/props";
import { currencyFormatter, percentFormatter, testBit } from "@/services/Utility";
import type { CellClassParams, ColDef, GridApi, GridReadyEvent, RowClassParams, RowHeightParams, ValueFormatterParams } from "ag-grid-community";
import { BudgetColumns, getCondensedBudgetColumnDefault } from "@/enums/BudgetColumns";
import { createFooterRow, showColumns, updateTotals } from "@/services/BudgetBuilderService";
import type { Project } from "@/types/Projects";
import type { CustomizableColumn } from "@/types/CustomizableColumns";

const drawItems = defineModel<DrawItem[]>("drawItems", { required: true });
const project = defineModel<Project>("project", { required: true });

const reloadTable = (): void => {
  //Updates weren't triggering for some reason, this is a hack to fix it
  showColumns(project.value.projectInfo, project.value.projectSettings.condensedBudgetColumns, gridApi);
  gridApi.value?.setGridOption("rowData", drawItems.value);
  updateTotals(footers, drawItems, gridApi);
};

defineExpose({ reloadTable });

const gridApi = ref<GridApi>();
const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

const rowClassRules = ref({
  "font-bold": (params: RowClassParams) => {
    return params.node.rowPinned != null;
  },
});

const getRowHeight = (params: RowHeightParams): number => {
  return params.node.rowPinned ? 25 : 35;
};

const resize = (): void => {
  gridApi.value?.sizeColumnsToFit();
};

const defaultColDefs = {
  floatingFilter: false,
  enableCellChangeFlash: false,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  suppressMovable: true,
  headerClass: "grid-align-center text-center",
  cellStyle: (params: CellClassParams) => {
    const data: DrawItem = params.data;
    if (data.fundsAvailable < 0) {
      return {
        color: "red",
      };
    }
    return null;
  },
};

const hideColumn = (column: CustomizableColumn): boolean => {
  const columns =
    project.value.projectSettings.condensedBudgetColumns == null
      ? getCondensedBudgetColumnDefault()
      : project.value.projectSettings.condensedBudgetColumns;
  return !testBit(columns, column.bit);
};

const colDefs = ref<ColDef[]>([
  {
    field: "lineRefNumber",
    cellDataType: "text",
    headerName: "Line #",
    headerClass: "grid-align-center",
    minWidth: 75,
    width: 75,
    pinned: true,
  },
  {
    field: "description",
    cellDataType: "text",
    headerName: "Description",
    headerClass: "grid-align-center",
    minWidth: 200,
    pinned: true,
    flex: 1,
  },
  {
    field: "custCostType",
    cellDataType: "text",
    headerName: "Category",
    cellClass: "text-center",
    minWidth: 100,
    width: 100,
  },
  {
    field: "costType",
    cellDataType: "text",
    headerName: "Cost Type",
    cellClass: "text-center",
    minWidth: 125,
    width: 125,
  },
  {
    field: "initialBudget",
    cellDataType: "number",
    headerName: "Initial Budget",
    headerClass: "text-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(BudgetColumns.initialBudget),
    minWidth: 125,
    width: 125,
  },
  {
    field: "budgetAmount",
    cellDataType: "number",
    headerName: "Budget Amount",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(BudgetColumns.budgetAmount),
    minWidth: 125,
    width: 125,
  },
  {
    field: "budgetAdjustment",
    cellDataType: "number",
    headerName: "Budget Adjustment",
    valueFormatter: currencyFormatter,
    minWidth: 125,
    width: 125,
  },
  {
    field: "cumulativeAdjustment",
    cellDataType: "number",
    headerName: "Cumulative Adjustment",
    headerClass: "text-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(BudgetColumns.cumulativeAdjustment),
    minWidth: 125,
    width: 125,
  },
  {
    field: "adjustedBudget",
    cellDataType: "number",
    headerName: "Adjusted Budget",
    valueFormatter: currencyFormatter,
    minWidth: 125,
    width: 125,
  },
  {
    field: "priorDrawnToDate",
    cellDataType: "number",
    headerName: "Prior Drawn To Date",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.priorDrawnToDate),
    minWidth: 125,
    width: 125,
  },
  {
    field: "totalThisDraw",
    cellDataType: "number",
    headerName: "This Draw Loan",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(BudgetColumns.totalThisDraw),
    minWidth: 125,
    width: 125,
  },
  {
    field: "totalThisEquity",
    cellDataType: "number",
    headerName: "This Draw Equity",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(BudgetColumns.totalThisEquity),
    minWidth: 125,
    width: 125,
  },
  {
    field: "netThisDraw",
    cellDataType: "number",
    headerName: "Net This Draw Loan",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.netThisDraw),
    minWidth: 125,
    width: 125,
  },
  {
    field: "priorEquityToDate",
    cellDataType: "number",
    headerName: "Prior Equity To Date",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.priorEquityToDate),
    minWidth: 125,
    width: 125,
  },
  {
    field: "percentDrawn",
    cellDataType: "number",
    headerName: "% Draw",
    valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, true),
    initialHide: hideColumn(BudgetColumns.percentDrawn),
    minWidth: 75,
    width: 75,
  },
  {
    field: "percentInspectedThisDraw",
    cellDataType: "number",
    headerName: "This Draw Insp",
    valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, params.data.custCostType == null || params.data.costType !== "INSP"),
    initialHide: hideColumn(BudgetColumns.percentInspectedThisDraw),
    minWidth: 75,
    width: 75,
  },
  {
    field: "projectedOver",
    cellDataType: "number",
    headerName: "Projected Over / (Under)",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.projectedOver),
    minWidth: 125,
    width: 125,
  },
  {
    field: "drawnToDate",
    cellDataType: "number",
    headerName: "Loan Drawn To Date",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.drawnToDate),
    minWidth: 125,
    width: 125,
  },
  {
    field: "netThisDrawEquity",
    cellDataType: "number",
    headerName: "Net This Draw Equity",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.netThisDrawEquity),
    minWidth: 125,
    width: 125,
  },
  {
    field: "equityToDate",
    cellDataType: "number",
    headerName: "Equity Drawn To Date",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.equityToDate),
    minWidth: 125,
    width: 125,
  },
  {
    field: "totalDrawnToDate",
    cellDataType: "number",
    headerName: "Total Drawn To Date",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.totalDrawnToDate),
    minWidth: 125,
    width: 125,
  },
  {
    field: "percentInspectedPriorDraw",
    cellDataType: "number",
    headerName: "Insp Prior Draw",
    valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, true),
    initialHide: hideColumn(BudgetColumns.percentInspectedPriorDraw),
    minWidth: 75,
    width: 75,
  },
  {
    field: "percentInspected",
    cellDataType: "number",
    headerName: "Total Insp",
    valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, true),
    initialHide: hideColumn(BudgetColumns.percentInspected),
    minWidth: 75,
    width: 75,
  },
  {
    field: "variance",
    cellDataType: "number",
    headerName: "Variance",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.variance),
    minWidth: 125,
    width: 125,
  },
  {
    field: "fundsAvailable",
    cellDataType: "number",
    headerName: "Funds Available",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.fundsAvailable),
    minWidth: 125,
    width: 125,
  },
  {
    field: "fundsRequired",
    cellDataType: "number",
    headerName: "Funds Required",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.fundsRequired),
    minWidth: 125,
    width: 125,
  },
  {
    field: "hccPercent",
    cellDataType: "number",
    headerName: "HCC %",
    valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, true),
    initialHide: hideColumn(BudgetColumns.hccPercent),
    minWidth: 75,
  },
  {
    field: "totalRetainagePriorDraw",
    cellDataType: "number",
    headerName: "Total Retainage Prior Draws",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.totalRetainagePriorDraw),
    minWidth: 125,
    width: 125,
  },
  {
    field: "totalReleasedPriorDraw",
    cellDataType: "number",
    headerName: "Total Released Prior Draws",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.totalReleasedPriorDraw),
    minWidth: 125,
    width: 125,
  },
  {
    field: "totalRetainage",
    cellDataType: "number",
    headerName: "Total Retainage",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.totalRetainage),
    minWidth: 125,
    width: 125,
  },

  {
    field: "totalReleased",
    cellDataType: "number",
    headerName: "Total Released",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.totalReleased),
    minWidth: 125,
    width: 125,
  },
  {
    field: "retainageBalance",
    cellDataType: "number",
    headerName: "Retainage Balance",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.retainageBalance),
    minWidth: 125,
    width: 125,
  },
]);

const footers = ref([createFooterRow("TOTAL")]);

onMounted(async (): Promise<void> => {
  updateTotals(footers, drawItems, gridApi);
});
</script>
